import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

const ACCENT = '#4d9fff';

const Container = styled.div`
  padding: 6rem 1.5rem 5rem;
  background: rgb(var(--bg));
  min-height: 100vh;
  max-width: 1180px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 5rem 1.25rem 4rem;
  }

  @media (max-width: 480px) {
    padding: 4.5rem 1rem 3rem;
  }
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem 1.75rem 2rem;
  border-radius: 22px;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));
  position: relative;
  overflow: hidden;

  &::after {
    content: "";
    position: absolute;
    inset: 0;
    background:
      radial-gradient(circle at 22% 18%, rgba(77, 159, 255, 0.18), transparent 48%),
      radial-gradient(circle at 78% 22%, rgba(77, 159, 255, 0.1), transparent 40%);
    pointer-events: none;
  }

  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 600px) {
    padding: 2rem 1.1rem 1.6rem;
    margin-bottom: 1.5rem;
    border-radius: 18px;
  }
`;

const Title = styled.h1`
  font-size: 2.6rem;
  color: rgb(var(--fg) / 0.96);
  margin: 0;
  line-height: 1.1;
  font-weight: 800;
  letter-spacing: -0.03em;

  @media (max-width: 600px) {
    font-size: 1.9rem;
  }
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: rgb(var(--fg) / 0.72);
  margin: 14px auto 14px;
  line-height: 1.65;
  max-width: 720px;

  @media (max-width: 600px) {
    font-size: 0.95rem;
  }
`;

const DatePublished = styled.p`
  font-size: 0.82rem;
  color: rgb(var(--gray) / 0.6);
  margin: 0 0 18px;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

const TagsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
`;

const Tag = styled.span`
  background: rgb(var(--fg) / 0.05);
  color: rgb(var(--fg) / 0.9);
  font-size: 0.78rem;
  padding: 6px 13px;
  border-radius: 999px;
  font-weight: 600;
  border: 1px solid rgba(77, 159, 255, 0.3);
  backdrop-filter: blur(4px);
  transition: transform 0.2s, background 0.2s, box-shadow 0.2s, border-color 0.2s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.08);
    border-color: rgba(77, 159, 255, 0.55);
    box-shadow: 0 10px 20px rgb(0 0 0 / calc(0.3 * var(--shadow-mul)));
  }
`;

const CoverImage = styled.img`
  display: block;
  width: 100%;
  max-width: 860px;
  height: 360px;
  object-fit: cover;
  object-position: center;
  margin: 1.5rem auto 1rem;
  border-radius: 18px;
  border: 1px solid rgb(var(--fg) / 0.08);
  box-shadow: 0 18px 36px rgb(0 0 0 / calc(0.45 * var(--shadow-mul)));
  background: rgb(var(--fg) / 0.04);
  filter: brightness(0.92) saturate(0.95);
  transition: filter 0.35s ease;

  &:hover {
    filter: brightness(1) saturate(1);
  }

  @media (max-width: 768px) {
    height: 260px;
  }

  @media (max-width: 480px) {
    height: 200px;
    max-width: 100%;
  }
`;

const Section = styled.section`
  margin-bottom: 1.5rem;
  padding: 1.75rem 1.6rem;
  background: rgb(var(--fg) / 0.035);
  border: 1px solid rgb(var(--fg) / 0.06);
  border-radius: 20px;
  backdrop-filter: blur(24px) saturate(1.15);
  -webkit-backdrop-filter: blur(24px) saturate(1.15);
  box-shadow: 0 24px 56px -16px rgb(0 0 0 / calc(0.5 * var(--shadow-mul)));

  @media (max-width: 600px) {
    margin-bottom: 1.1rem;
    padding: 1.25rem 1.05rem;
    border-radius: 16px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 1.4rem;
  color: ${ACCENT};
  margin: 0 0 14px;
  border-bottom: 1px solid rgba(77, 159, 255, 0.25);
  padding-bottom: 10px;
  font-weight: 800;
  letter-spacing: -0.02em;

  @media (max-width: 600px) {
    font-size: 1.15rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.85;
  color: rgb(var(--fg) / 0.82);
  margin: 0 0 1rem;

  &:last-child {
    margin-bottom: 0;
  }

  a {
    color: ${ACCENT};
    text-decoration: none;
    border-bottom: 1px solid rgba(77, 159, 255, 0.35);
    transition: border-color 0.2s;
  }

  a:hover {
    border-bottom-color: ${ACCENT};
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
`;

const ListItem = styled.li`
  position: relative;
  padding-left: 1.5rem;
  line-height: 1.7;
  color: rgb(var(--fg) / 0.82);
  font-size: 1rem;

  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 0.7em;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: rgba(77, 159, 255, 0.7);
    box-shadow: 0 0 8px rgba(77, 159, 255, 0.45);
  }

  strong {
    color: rgb(var(--fg) / 0.95);
    font-weight: 700;
  }

  a {
    color: ${ACCENT};
    text-decoration: none;
    border-bottom: 1px solid rgba(77, 159, 255, 0.3);
    transition: border-color 0.2s;
  }

  a:hover {
    border-bottom-color: ${ACCENT};
  }
`;

const ClusterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
  margin-top: 0.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ClusterCard = styled.div`
  padding: 1.25rem 1.15rem;
  background: rgb(var(--fg) / 0.04);
  border: 1px solid rgba(77, 159, 255, 0.2);
  border-radius: 16px;
`;

const ClusterLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${ACCENT};
  margin-bottom: 0.5rem;
`;

const ClusterName = styled.h4`
  font-size: 1rem;
  color: rgb(var(--fg) / 0.95);
  margin: 0 0 0.6rem;
  font-weight: 700;
  letter-spacing: -0.01em;
`;

const ClusterDesc = styled.p`
  margin: 0;
  font-size: 0.92rem;
  line-height: 1.65;
  color: rgb(var(--fg) / 0.78);
`;

const EmbedFrame = styled.div`
  padding: 1rem;
  border-radius: 16px;
  background: rgb(0 0 0 / calc(0.42 * var(--shadow-mul)));
  border: 1px solid rgb(var(--fg) / 0.06);

  iframe {
    display: block;
    width: 100%;
    border: 0;
    border-radius: 10px;
    background: #0a0d12;
  }
`;

const EmbedLabel = styled.p`
  margin: 0 0 0.6rem;
  font-size: 0.7rem;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${ACCENT};
  font-weight: 700;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
  padding-top: 0.5rem;
`;

const BackButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.9rem 2.2rem;
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgb(var(--fg) / 0.92);
  background: rgb(var(--fg) / 0.06);
  border: 1px solid rgb(var(--fg) / 0.14);
  border-radius: 999px;
  cursor: pointer;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  transition: transform 0.25s, border-color 0.25s, background 0.25s;

  &:hover {
    transform: translateY(-1px);
    background: rgb(var(--fg) / 0.1);
    border-color: rgba(77, 159, 255, 0.5);
  }
`;

const clusters = [
  {
    label: 'Cluster 0',
    name: 'High-Energy Tracks',
    desc: 'More beats per minute, energy, loudness, and liveness, with the least acousticness.',
  },
  {
    label: 'Cluster 1',
    name: 'Bright & Danceable',
    desc: 'Highest valence of the three — positive, happy-sounding tracks with high danceability.',
  },
  {
    label: 'Cluster 2',
    name: 'Acoustic & Mellow',
    desc: 'More acousticness, quieter tracks with lower valence, energy, and liveness.',
  },
];

const BbdaClustering = () => {
  const tags = ['Python', 'Machine Learning', 'Clustering', 'Data Visualization'];
  const datePublished = '2024-07-20';
  const navigate = useNavigate();

  return (
    <Container>
      <Header>
        <Title>Data-Driven Insights for Music Personalization</Title>
        <Subtitle>
          K-Means clustering over Spotify's top 2000 tracks — PCA, the elbow method, and interpretation
          of the resulting audio-feature clusters.
        </Subtitle>
        <DatePublished>Published on {datePublished}</DatePublished>
        <CoverImage src="/images/bbda_clustering.png" alt="Clustering Analysis" />
        <TagsContainer>
          {tags.map((tag, index) => (
            <Tag key={index}>{tag}</Tag>
          ))}
        </TagsContainer>
      </Header>

      <Section>
        <SectionTitle>Collaborators</SectionTitle>
        <List>
          <ListItem>
            <a href="https://www.linkedin.com/in/alexandra-segu/" target="_blank" rel="noopener noreferrer">
              Alexandra Segkou
            </a>
          </ListItem>
          <ListItem>
            <a href="https://www.linkedin.com/in/jimmysiloglou/" target="_blank" rel="noopener noreferrer">
              Dimitris Mysiloglou
            </a>
          </ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Overview</SectionTitle>
        <Paragraph>
          This project was conducted as part of the Data Science Bootcamp from Big Blue Data Academy. It was the fourth and final practice project of the course, completed in around 5 hours on the 20th of July 2024. The topic was unsupervised machine learning, and my teammates and I performed clustering on a dataset of Spotify's top 2000 songs, containing audio features such as Danceability, BPM, Liveness, and Valence.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>Objectives</SectionTitle>
        <Paragraph>The primary objectives of this project were:</Paragraph>
        <List>
          <ListItem>To explore and understand the key characteristics of these top tracks.</ListItem>
          <ListItem>To cluster songs based on their audio features to identify distinct groups.</ListItem>
          <ListItem>Identify patterns and group similar songs to better understand our music collection.</ListItem>
          <ListItem>Make informed decisions for marketing, playlist curation, and audience engagement based on the analysis.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Tools Used</SectionTitle>
        <Paragraph>The following tools and libraries were utilized in this project:</Paragraph>
        <List>
          <ListItem><strong>Python</strong></ListItem>
          <ListItem><strong>Pandas</strong> and <strong>NumPy</strong> for data manipulation</ListItem>
          <ListItem><strong>Matplotlib</strong>, <strong>Seaborn</strong>, and <strong>WordCloud</strong> for visualizations</ListItem>
          <ListItem><strong>Scikit-learn</strong> for preprocessing, decomposition, and clustering</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Methodology</SectionTitle>
        <Paragraph>
          The dataset was extracted from Spotify by @plamere and downloaded by us from{' '}
          <a href="https://www.kaggle.com/datasets/iamsumat/spotify-top-2000s-mega-dataset/data" target="_blank" rel="noopener noreferrer">
            Kaggle
          </a>
          . We applied clustering techniques to group songs based on audio features.
        </Paragraph>
        <Paragraph>The steps involved in the analysis were:</Paragraph>
        <List>
          <ListItem><strong>Data Preprocessing:</strong> Cleaning and scaling the data for analysis.</ListItem>
          <ListItem><strong>Dimensionality Reduction:</strong> Using PCA to reduce the number of features.</ListItem>
          <ListItem><strong>Elbow Method:</strong> Determining the optimal number of clusters.</ListItem>
          <ListItem><strong>Clustering:</strong> Applying the K-Means algorithm to group songs.</ListItem>
          <ListItem><strong>Analysis:</strong> Interpreting the clusters and identifying key characteristics.</ListItem>
        </List>
      </Section>

      <Section>
        <SectionTitle>Results</SectionTitle>
        <Paragraph>Our approach yielded three distinct clusters of songs based on their audio features:</Paragraph>
        <ClusterGrid>
          {clusters.map((c) => (
            <ClusterCard key={c.label}>
              <ClusterLabel>{c.label}</ClusterLabel>
              <ClusterName>{c.name}</ClusterName>
              <ClusterDesc>{c.desc}</ClusterDesc>
            </ClusterCard>
          ))}
        </ClusterGrid>
      </Section>

      <Section>
        <SectionTitle>Presentation</SectionTitle>
        <Paragraph>Below is an embedded presentation summarizing the findings from the sentiment and topic analysis project.</Paragraph>
        <EmbedFrame>
          <EmbedLabel>Slides</EmbedLabel>
          <iframe
            title="Unsupervised Learning Presentation"
            src="https://docs.google.com/presentation/d/e/2PACX-1vTdyJ2WJiyp-Bc-xErrNIQbUvxwn38MONmd4zc8R49QmaX0rtYfjQuUq0vO6Sj46w/embed?start=false&loop=false&delayms=3000"
            width="100%"
            height="569"
            allowFullScreen
            mozallowfullscreen="true"
            webkitallowfullscreen="true"
          />
        </EmbedFrame>
      </Section>

      <Section>
        <SectionTitle>Conclusion</SectionTitle>
        <Paragraph>
          The project successfully grouped the songs into three clusters based on their audio features. These clusters were analyzed and visualized to understand the characteristics of each group. This information can inform decisions for marketing, playlist curation, and audience engagement.
        </Paragraph>
      </Section>

      <ButtonRow>
        <BackButton onClick={() => navigate('/projects')}>← Back to Projects</BackButton>
      </ButtonRow>
    </Container>
  );
};

export const BbdaClusteringMetadata = {
  title: 'Data-Driven Insights for Music Personalization',
  description: 'Utilized unsupervised machine learning to cluster Spotify\'s top 2000 songs based on their audio features.',
  tags: ['Python', 'Machine Learning', 'Clustering', 'Data Visualization'],
  languages: ['Python', 'GitHub'],
  datePublished: '2024-07-20',
  path: '/projects/bbda-clustering',
  coverImage: 'images/bbda_clustering.png',
};

export default BbdaClustering;
